import React, { useState } from 'react';
import { Building2, User, Tag, CheckCircle2, Clock, XCircle, MessageSquare, ExternalLink } from 'lucide-react';
import { Founder } from '../data/founders';
import { motion, AnimatePresence } from 'framer-motion';
interface FounderCardProps {
  founder: Founder;
}
type ActionState = 'interested' | 'follow-up' | 'pass' | null;
export function FounderCard({
  founder
}: FounderCardProps) {
  const [action, setAction] = useState<ActionState>(null);
  const [notes, setNotes] = useState('');
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const handleAction = (newAction: ActionState) => {
    setAction(current => current === newAction ? null : newAction);
  };
  return <motion.div layout initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className={`glass-card rounded-2xl p-5 md:p-6 h-full flex flex-col transition-all duration-300 ${action === 'interested' ? 'border-green-500/50 glow-green bg-green-500/5' : action === 'follow-up' ? 'border-amber-500/50 glow-amber bg-amber-500/5' : action === 'pass' ? 'border-white/5 opacity-60 grayscale-[0.5]' : 'border-white/10 hover:border-white/20'}`} data-id="element-29">
      {/* Top section: Avatar + Info */}
      <div className="flex items-center gap-4 mb-3 md:mb-4" data-id="element-30">
        {/* Avatar / Logo */}
        {founder.image ? <div className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 overflow-hidden border-2 border-white/15 ${founder.imageType === 'logo' ? 'rounded-xl bg-white/10 p-1.5' : 'rounded-full'}`} data-id="element-31">
            <img src={founder.image} alt={founder.imageType === 'logo' ? `${founder.company} logo` : founder.founder} className={`w-full h-full ${founder.imageType === 'logo' ? 'object-contain' : 'object-cover'}`} data-id="element-32" />
          </div> : <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-accent-cyan/20 to-accent-pink/20 border-2 border-white/10 flex items-center justify-center" data-id="element-33">
            <User className="w-7 h-7 md:w-8 md:h-8 text-white/40" data-id="element-34" />
          </div>}

        {/* Name info — no truncation */}
        <div className="flex-1 min-w-0" data-id="element-35">
          <h3 className="font-sora font-bold text-base md:text-lg text-white mb-1 leading-tight" data-id="element-36">
            {founder.company}
          </h3>
          <p className="font-inter text-white/70 text-sm md:text-base leading-snug" data-id="element-37">
            {founder.founder}
          </p>
        </div>
      </div>

      {/* Sector badge + Company logo */}
      <div className="mb-3 md:mb-4 flex items-center justify-between gap-3" data-id="element-38">
        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs md:text-sm font-medium bg-white/10 text-white/90 border border-white/10" data-id="element-39">
          <Tag className="w-3 h-3 mr-1.5 opacity-70" data-id="element-40" />
          {founder.sector}
        </span>
        {founder.logo && (founder.company === 'Cotriply' ? <div className="flex items-center gap-2" data-id="element-41">
              <img src={founder.logo} alt={`${founder.company} logo`} className="w-auto h-8 md:h-10 object-contain opacity-80" data-id="element-42" />
              <span className="font-sora font-bold text-white text-lg md:text-xl" data-id="element-43">
                Cotriply
              </span>
            </div> : <img src={founder.logo} alt={`${founder.company} logo`} className={`w-auto object-contain opacity-80 ${founder.logoScale === 'large' ? 'h-14 md:h-20 max-w-[180px] md:max-w-[220px]' : 'h-10 md:h-14 max-w-[130px] md:max-w-[160px]'}`} data-id="element-44" />)}
      </div>

      {/* Website link */}
      {founder.website && <a href={founder.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs md:text-sm text-accent-cyan hover:text-white transition-colors mb-4 md:mb-5 group" data-id="element-45">
          <ExternalLink className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" data-id="element-46" />
          Visit Website
        </a>}

      {/* Action buttons */}
      <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4 mt-auto" data-id="element-47">
        <button onClick={() => handleAction('interested')} className={`flex flex-col items-center justify-center py-2.5 md:py-3 px-1 rounded-lg border transition-all ${action === 'interested' ? 'bg-green-500/20 border-green-500 text-green-400' : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'}`} data-id="element-48">
          <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 mb-1 md:mb-2" data-id="element-49" />
          <span className="text-[10px] md:text-xs font-semibold uppercase tracking-wider" data-id="element-50">
            Interested
          </span>
        </button>

        <button onClick={() => handleAction('follow-up')} className={`flex flex-col items-center justify-center py-2.5 md:py-3 px-1 rounded-lg border transition-all ${action === 'follow-up' ? 'bg-amber-500/20 border-amber-500 text-amber-400' : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'}`} data-id="element-51">
          <Clock className="w-5 h-5 md:w-6 md:h-6 mb-1 md:mb-2" data-id="element-52" />
          <span className="text-[10px] md:text-xs font-semibold uppercase tracking-wider" data-id="element-53">
            Follow-up
          </span>
        </button>

        <button onClick={() => handleAction('pass')} className={`flex flex-col items-center justify-center py-2.5 md:py-3 px-1 rounded-lg border transition-all ${action === 'pass' ? 'bg-white/20 border-white/40 text-white' : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'}`} data-id="element-54">
          <XCircle className="w-5 h-5 md:w-6 md:h-6 mb-1 md:mb-2" data-id="element-55" />
          <span className="text-[10px] md:text-xs font-semibold uppercase tracking-wider" data-id="element-56">
            Pass
          </span>
        </button>
      </div>

      {/* Notes */}
      <div className="mt-2" data-id="element-57">
        {!isNotesExpanded ? <button onClick={() => setIsNotesExpanded(true)} className="w-full flex items-center justify-center py-2.5 md:py-3 text-sm md:text-base text-white/50 hover:text-white/80 transition-colors border border-dashed border-white/20 rounded-lg bg-white/5" data-id="element-58">
            <MessageSquare className="w-4 h-4 md:w-5 md:h-5 mr-2" data-id="element-59" />
            {notes ? 'Edit notes' : 'Add notes'}
          </button> : <AnimatePresence data-id="element-60">
            <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} data-id="element-61">
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Capture follow-up interest..." className="w-full bg-black/20 border border-white/10 rounded-lg p-3 md:p-4 text-sm md:text-base text-white placeholder:text-white/30 focus:outline-none focus:border-accent-cyan/50 focus:ring-1 focus:ring-accent-cyan/50 transition-all resize-none min-h-[80px] md:min-h-[100px]" autoFocus data-id="element-62" />
              <div className="flex justify-end mt-2" data-id="element-63">
                <button onClick={() => setIsNotesExpanded(false)} className="text-xs md:text-sm font-medium text-accent-cyan hover:text-white transition-colors px-3 py-1.5 md:px-4 md:py-2 bg-accent-cyan/10 rounded-full" data-id="element-64">
                  Done
                </button>
              </div>
            </motion.div>
          </AnimatePresence>}
      </div>
    </motion.div>;
}